import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import useMessageService from '../../services/i18n/useMessageService';
import Button from '../../components/Button';
import useErrorPageTitleAndDescriptionMessageKeys from './useErrorPageTitleAndDescriptionMessageKeys';
import PageContainer from '../../components/PageContainer';
import { UnauthenticatedPaths } from '../../constants/Paths';
import getTargetAttribute from '../../services/getTargetAttribute';
import useUrlQueryParams from '../../services/useUrlQueryParams';
import getFormattedDateTime from '../../services/getFormattedDateTime';
import SearchParams from '../../constants/SearchParams';

const isInteger = (value) => {
    return /^\d+$/.test(value);
};

const ErrorPage = () => {
    const history = useHistory();
    const errorMessageKeys = useErrorPageTitleAndDescriptionMessageKeys();
    const searchParams = useUrlQueryParams();
    const [[dateLabel = 'Date:', errorIdLabel = 'Error ID:']] = useMessageService([
        'general.date_label',
        'general.error_id_label',
    ]);
    const [
        [
            title = 'An unexpected error occurred',
            description = 'The page you are trying to access is not valid.',
            buttonLabel = 'Back to Login',
        ],
    ] = useMessageService([...errorMessageKeys, 'general.back_to_login']);

    const errorId = searchParams.get(SearchParams.ERROR_ID);
    const timestampParam = searchParams.get(SearchParams.ERROR_TIMESTAMP);
    const timestamp = isInteger(timestampParam) ? parseInt(timestampParam, 10) : timestampParam;

    const errorMetaDataFieldCSS = ({ grayLighter }) =>
        css`
            color: ${grayLighter};
            font-size: 14px;
            text-align: left;
            
            span:first-of-type {
                margin-right: 4px;
            }

            :not(:last-of-type) {
                margin-bottom: 0.667rem;
            }
            
            :first-of-type {
                margin-top: 1.667rem;
            }
        }
        `;

    return (
        <PageContainer title={title}>
            <span
                css={css`
                    display: flex;
                    font-size: 1.5rem;
                    margin-bottom: 3.33rem;
                    justify-content: center;
                    text-align: left;
                `}
            >
                {description}
            </span>
            <Button
                type="button"
                onClick={() => {
                    history.push(UnauthenticatedPaths.LOGIN);
                }}
                data-target={getTargetAttribute('login-page-button')}
            >
                {buttonLabel}
            </Button>
            {timestamp && (
                <div css={errorMetaDataFieldCSS}>
                    <span>
                        <strong>{dateLabel}</strong>
                    </span>
                    <span>{getFormattedDateTime(timestamp)}</span>
                </div>
            )}
            {errorId && (
                <div css={errorMetaDataFieldCSS}>
                    <span>
                        <strong>{errorIdLabel}</strong>
                    </span>
                    <span>{errorId}</span>
                </div>
            )}
        </PageContainer>
    );
};

export default ErrorPage;
